import * as React from 'react';

import { enumCurrencyCode } from '@rematter/frontend-lib-api-client';
import { humanReadableShortPricingUnit } from '@rematter/ui-web';

import { UnitPriceFieldCellProps } from '../types';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { toMoney } from '~/modules/trade/utils/money';
import { PriceFieldCell } from '~/shared/components/line-items';

const FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 8,
};

export const UnitPriceFieldCell: React.FC<UnitPriceFieldCellProps> = ({
  lineItem,
  disabled = false,
  onUpdateLineItem,
  showPricingUnitSuffix = false,
  errors,
}) => {
  const t = useTranslations();
  const netWeight = lineItem.weights?.[enumLoadWeightType.Net];

  const handleChange = React.useCallback(
    (value: number) => {
      const currencyCode =
        lineItem.unitPrice?.currencyCode ?? enumCurrencyCode.USD;

      onUpdateLineItem(lineItem.id, {
        unitPrice: toMoney({
          amount: value,
          currencyCode,
        }),
        estimatedPrice: toMoney({
          amount: value * (netWeight?.value ?? 0),
          currencyCode,
        }),
      });
    },
    [
      lineItem.id,
      lineItem.unitPrice?.currencyCode,
      netWeight?.value,
      onUpdateLineItem,
    ],
  );

  if (lineItem.unitPrice == null && lineItem.itemId) {
    return (
      <div className="@UnitPriceFieldCell flex h-full items-center justify-end px-2 text-sm text-neutral/40">
        {t.scaleTicketPro.columns.priceTbd}
      </div>
    );
  }

  return (
    <PriceFieldCell
      value={lineItem.unitPrice?.amount ?? 0}
      onChange={handleChange}
      disabled={disabled}
      suffix={
        showPricingUnitSuffix && lineItem.pricingUnit
          ? `/${humanReadableShortPricingUnit(lineItem.pricingUnit)}`
          : undefined
      }
      formatOptions={FORMAT_OPTIONS}
      errorMessage={errors?.unitPrice}
      isInvalid={!!errors?.unitPrice}
      className="@UnitPriceFieldCell"
    />
  );
};
